// src/components/CoverPage/DocumentationPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Button from "./Button";
import "./DocsPage.css";

const DocumentationPage = () => {
  const [activeSection, setActiveSection] = useState("");
  const sectionRefs = useRef({});

  // Register refs for each section
  const registerSectionRef = (id, ref) => {
    sectionRefs.current[id] = ref;
  };

  // Scroll to section when clicked in sidebar
  const scrollToSection = (sectionId) => {
    const sectionRef = sectionRefs.current[sectionId];
    if (sectionRef) {
      sectionRef.scrollIntoView({ behavior: "smooth" });
      setActiveSection(sectionId);
    }
  };

  // Update active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections = Object.keys(sectionRefs.current);
      
      for (const section of sections) {
        const ref = sectionRefs.current[section];
        if (!ref) continue;
        
        const rect = ref.getBoundingClientRect();
        if (rect.top <= 100 && rect.bottom >= 100) {
          setActiveSection(section);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Define the table of contents
  const tableOfContents = [
    { id: "introduction", title: "Introduction" },
    { id: "protocol-overview", title: "Protocol Overview", 
      subsections: [
        { id: "dual-token-system", title: "Dual-Token System" },
        { id: "fund-architecture", title: "Fund Architecture" }
      ]
    },
    { id: "smart-contract-architecture", title: "Smart Contract Architecture",
      subsections: [
        { id: "mainxxfund-contract", title: "MainXXFund Contract" },
        { id: "mainxxfundwithgovernance-contract", title: "MainXXFundWithGovernance Contract" },
        { id: "yildxtoken-contract", title: "YILDXToken Contract" },
        { id: "yildxgovernance-contract", title: "YILDXGovernance Contract" }
      ]
    },
    { id: "investment-process", title: "Investment Process",
      subsections: [
        { id: "depositing-eth", title: "Depositing ETH" },
        { id: "minting-fund-tokens", title: "Minting Fund Tokens" },
        { id: "asset-allocation", title: "Asset Allocation" },
        { id: "redemption-process", title: "Redemption Process" }
      ]
    },
    { id: "fee-structure", title: "Fee Structure",
      subsections: [
        { id: "transaction-fees", title: "Transaction Fees" },
        { id: "management-fees", title: "Management Fees" },
        { id: "fee-distribution", title: "Fee Distribution" },
        { id: "gas-fund", title: "Gas Fund" }
      ]
    },
    { id: "governance-system", title: "Governance System",
      subsections: [
        { id: "voting-power", title: "Voting Power" },
        { id: "proposal-system", title: "Proposal System" },
        { id: "auto-approval-mechanism", title: "Auto-Approval Mechanism" },
        { id: "delegation-system", title: "Delegation System" }
      ]
    },
    { id: "security-considerations", title: "Security Considerations",
      subsections: [
        { id: "smart-contract-security", title: "Smart Contract Security" },
        { id: "re-entrancy-protection", title: "Re-entrancy Protection" },
        { id: "fee-safeguards", title: "Fee Safeguards" }
      ]
    },
    { id: "token-economics", title: "Token Economics",
      subsections: [
        { id: "mcif-token-fund-token", title: "MCIF Token (Fund Token)" },
        { id: "yildx-token-governance-token", title: "YILDX Token (Governance Token)" },
        { id: "YildX-Token", title: "YildX Token" }
      ]
    },
    { id: "competitive-analysis", title: "Competitive Analysis" },
    { id: "roadmap", title: "Roadmap" },
    { id: "conclusion", title: "Conclusion" },
    { id: "references", title: "References" }
  ];

  // Section component
  const Section = ({ id, title, children }) => {
    const ref = useRef(null);

    useEffect(() => {
      registerSectionRef(id, ref.current);
    }, [id]);

    return (
      <section ref={ref} id={id} className="docs-section">
        <h2 className="text-3xl font-bold text-indigo-900 mb-6">{title}</h2>
        {children}
      </section>
    );
  };

  // Subsection component
  const Subsection = ({ id, title, children }) => {
    const ref = useRef(null);

    useEffect(() => {
      registerSectionRef(id, ref.current);
    }, [id]);

    return (
      <div ref={ref} id={id} className="docs-subsection mb-12">
        <h3 className="text-2xl font-semibold text-indigo-800 mb-4">{title}</h3>
        {children}
      </div>
    );
  };

  return (
    <main className="docs-page min-h-screen bg-gray-50">
      {/* Top navigation bar */}
      <header className="bg-indigo-800 text-white shadow-md sticky top-0 z-10">
        <div className="max-w-screen-2xl mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="text-2xl font-bold">YildX</Link>
          <div className="flex gap-4">
            <Link to="/app">
              <Button variant="secondary" className="py-2 px-4 min-w-0">See App</Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-screen-2xl mx-auto px-4 py-8 flex">
        {/* Sidebar navigation */}
        <aside className="docs-sidebar w-64 flex-shrink-0 pr-8 pt-8">
          <div className="sticky top-24 overflow-y-auto max-h-[calc(100vh-6rem)]">
            <h2 className="text-xl font-bold mb-4 text-indigo-900">Documentation</h2>
            <nav>
              <ul className="space-y-1">
                {tableOfContents.map((section) => (
                  <li key={section.id}>
                    <button
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left py-2 px-3 rounded-md hover:bg-indigo-50 ${
                        activeSection === section.id ? "bg-indigo-100 text-indigo-800 font-medium" : "text-gray-600"
                      }`}
                    >
                      {section.title}
                    </button>
                    
                    {section.subsections && (
                      <ul className="ml-4 mt-1 space-y-1 border-l-2 border-indigo-100 pl-2">
                        {section.subsections.map((subsection) => (
                          <li key={subsection.id}>
                            <button
                              onClick={() => scrollToSection(subsection.id)}
                              className={`w-full text-left py-1 px-3 rounded-md hover:bg-indigo-50 text-sm ${
                                activeSection === subsection.id ? "bg-indigo-100 text-indigo-800 font-medium" : "text-gray-600"
                              }`}
                            >
                              {subsection.title}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <div className="docs-content flex-1 max-w-3xl bg-white p-8 rounded-lg shadow-sm">
          <div className="prose prose-indigo max-w-none">
            <h1 className="text-4xl font-bold text-indigo-900 mb-8">YildX: A Decentralized Crypto Index Fund with Governance</h1>
            
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-100 mb-8">
              <h3 className="font-bold text-blue-800 mb-2">Abstract</h3>
              <p className="text-gray-700">
                YildX introduces a novel approach to cryptocurrency investment through a decentralized index fund with robust governance. This documentation outlines the technical architecture of the YildX protocol, explaining its dual-token system, fee distribution mechanism, governance structure, and auto-approval voting system. By combining the diversification benefits of index funds with decentralized governance and fee distribution, YildX creates a sustainable ecosystem that aligns the interests of all participants while maintaining the professional management capabilities of traditional finance.
              </p>
            </div>

            <Section id="introduction" title="Introduction">
              <p className="mb-4">
                The cryptocurrency market offers tremendous opportunities but presents significant challenges for individual investors—volatile prices, complex technology, diverse ecosystems, and the need for constant monitoring. Traditional finance has long addressed similar challenges through index funds, which provide diversification, professional management, and reduced costs. However, the centralized nature of conventional index funds contradicts the ethos of decentralized finance.
              </p>
              <p>
                YildX bridges this gap by creating a decentralized crypto index fund that maintains the benefits of professional management while distributing control and fee income to token holders through a unique governance model. This approach creates a self-reinforcing ecosystem where all participants benefit from the fund's growth and success.
              </p>
            </Section>

            <Section id="protocol-overview" title="Protocol Overview">
              <Subsection id="dual-token-system" title="Dual-Token System">
                <p className="mb-4">YildX operates with a dual-token system:</p>
                <ol className="list-decimal pl-6 space-y-4 mb-4">
                  <li>
                    <strong>MCIF (Fund Token):</strong> Represents a proportional share of the underlying assets in the index fund. These tokens track the net asset value (NAV) of the fund and can be redeemed for ETH or specific underlying assets.
                  </li>
                  <li>
                    <strong>YILDX (Governance Token):</strong> Provides governance rights and fee income to holders. YILDX tokens grant proportional voting power in fund governance.
                    <ul className="list-disc pl-6 mt-2">
                      <li>60,000,000 Total Supply</li>
                      <li>15,000,000 Available for sale</li>
                      <li>Voting power proportional to token holdings</li>
                    </ul>
                  </li>
                </ol>
              </Subsection>

              <Subsection id="fund-architecture" title="Fund Architecture">
                <p className="mb-4">The YildX protocol is built on a series of interconnected smart contracts that handle:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Asset management and allocation</li>
                  <li>Token minting and redemption</li>
                  <li>Fee collection and distribution</li>
                  <li>Governance proposal submission and voting</li>
                  <li>Auto-execution of approved proposals</li>
                </ul>
                <p className="mt-4">
                  The protocol integrates with decentralized exchanges for asset purchases and sales, ensuring that all operations remain fully on-chain and transparent.
                </p>
              </Subsection>
            </Section>

            <Section id="smart-contract-architecture" title="Smart Contract Architecture">
              <Subsection id="mainxxfund-contract" title="MainXXFund Contract">
                <p className="mb-4">The base contract that implements the core functionality of the index fund includes settings for:</p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>Management fee (2% annual)</li>
                  <li>Entry fee (0.25%)</li>
                  <li>Exit fee (0.25%)</li>
                  <li>Slippage tolerance (3%)</li>
                  <li>Token information storage</li>
                </ul>
                
                <p className="mb-4">Key features include:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>ERC20 implementation for the fund token</li>
                  <li>Token allocation tracking</li>
                  <li>ETH deposits and withdrawals</li>
                  <li>Fund token minting and redemption</li>
                  <li>Manual and automatic rebalancing</li>
                  <li>Fee collection mechanisms</li>
                  <li>Single-asset redemption options</li>
                </ul>
              </Subsection>

              <Subsection id="mainxxfundwithgovernance-contract" title="MainXXFundWithGovernance Contract">
                <p className="mb-4">Extends the base fund contract with governance integration, including:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Connection to the governance token</li>
                  <li>Connection to the governance contract</li>
                  <li>Fee collection and distribution tracking</li>
                  <li>Methods for distributing fees to governance token holders</li>
                </ul>
                <p className="mt-4">
                  This contract redirects fees to the governance token holders and adds permission checks for governance-controlled functions.
                </p>
              </Subsection>

              <Subsection id="yildxtoken-contract" title="YILDXToken Contract">
                <p className="mb-4">The governance token implementation features:</p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>Total supply of 60 million tokens</li>
                  <li>Initial sale amount of 15 million tokens</li>
                  <li>Fee distribution tracking</li>
                  <li>Fee claim tracking for each holder</li>
                  <li>Token sale functionality with tiered pricing</li>
                </ul>
                
                <p className="mb-4">Key features include:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>On-chain voting capability (ERC20Votes)</li>
                  <li>Fee distribution mechanism</li>
                  <li>Claim tracking for each holder</li>
                  <li>Token sale functionality</li>
                </ul>
              </Subsection>

              <Subsection id="yildxgovernance-contract" title="YILDXGovernance Contract">
                <p className="mb-4">Implements the governance system with capabilities for:</p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>Various proposal types (fee changes, token management, etc.)</li>
                  <li>Proposal tracking</li>
                  <li>Voting management</li>
                  <li>Auto-approval mechanisms</li>
                </ul>
                
                <p className="mb-4">Key features include:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Proposal creation and tracking</li>
                  <li>Voting and vote weight management</li>
                  <li>Auto-approval mechanism</li>
                  <li>Execution delay handling</li>
                  <li>Threshold management for different proposal types</li>
                  <li>Delegator profiles and delegation system</li>
                </ul>
              </Subsection>
            </Section>

            <Section id="investment-process" title="Investment Process">
              <Subsection id="depositing-eth" title="Depositing ETH">
                <p>
                  Investors begin by depositing ETH into the fund contract. Deposits are tracked per user and can be withdrawn before minting if desired.
                </p>
              </Subsection>

              <Subsection id="minting-fund-tokens" title="Minting Fund Tokens">
                <p className="mb-4">After depositing, users can mint MCIF fund tokens. This process:</p>
                <ol className="list-decimal pl-6 space-y-2">
                  <li>Calculates and deducts the entry fee</li>
                  <li>Distributes fee between gas fund (30%) and governance token holders (70%)</li>
                  <li>Uses the remaining ETH to purchase the underlying assets</li>
                  <li>Mints MCIF tokens based on the current share price</li>
                  <li>Assigns tokens to the user within the fund</li>
                </ol>
              </Subsection>

              <Subsection id="asset-allocation" title="Asset Allocation">
                <p className="mb-4">
                  The fund's assets are allocated according to the defined allocations for each token, with allocation changes requiring:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>Token address verification</li>
                  <li>Price feed verification</li>
                  <li>Allocation limit checks (max 100% total)</li>
                  <li>Updates to trading helpers</li>
                </ul>
                <p>
                  The asset allocation is managed through governance proposals once the fund is active, allowing governance to add, remove, or adjust token allocations based on market conditions and investment strategies.
                </p>
              </Subsection>

              <Subsection id="redemption-process" title="Redemption Process">
                <p className="mb-4">Users can redeem their fund tokens in several ways:</p>
                <ol className="list-decimal pl-6 space-y-2 mb-4">
                  <li><strong>ETH Redemption:</strong> Convert fund tokens back to ETH</li>
                  <li><strong>Specific Token Redemption:</strong> Redeem for a specific underlying asset</li>
                  <li><strong>Token Withdrawal:</strong> Move fund tokens to the user's wallet</li>
                </ol>
                
                <p className="mb-4">The redemption process handles:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Selling underlying assets</li>
                  <li>Calculating exit fees</li>
                  <li>Splitting fees between gas fund and governance token holders</li>
                  <li>Transferring ETH back to the user</li>
                </ul>
                <p>
                  Each redemption method includes fee calculation and distribution, ensuring that governance token holders receive their share of exit fees.
                </p>
              </Subsection>
            </Section>

            <Section id="fee-structure" title="Fee Structure">
              <Subsection id="transaction-fees" title="Transaction Fees">
                <p className="mb-4">YildX implements two types of transaction fees:</p>
                <ol className="list-decimal pl-6 space-y-2 mb-4">
                  <li><strong>Entry Fee (0.25%):</strong> Applied when users mint fund tokens</li>
                  <li><strong>Exit Fee (0.25%):</strong> Applied when users redeem fund tokens</li>
                </ol>
                
                <p className="mb-4">These fees are calculated as a percentage of the transaction value and are intended to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Discourage excessive trading that could harm the fund</li>
                  <li>Cover gas costs for purchasing and selling assets</li>
                  <li>Provide revenue for governance token holders</li>
                  <li>Maintain the gas fund for operational expenses</li>
                </ul>
              </Subsection>

              <Subsection id="management-fees" title="Management Fees">
                <p className="mb-4">
                  A 2% annual management fee is charged on the total assets under management. This fee is:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Collected periodically (no more than once per day)</li>
                  <li>Calculated based on the time since last collection</li>
                  <li>Added to the fund's total supply, diluting existing holders</li>
                  <li>Distributed to governance token holders</li>
                </ul>
              </Subsection>

              <Subsection id="fee-distribution" title="Fee Distribution">
                <p className="mb-4">All collected fees are split between:</p>
                <ol className="list-decimal pl-6 space-y-2 mb-4">
                  <li><strong>Governance Token Holders (70%):</strong> Distributed proportionally to YILDX holders</li>
                  <li><strong>Gas Fund (30%):</strong> Retained for paying transaction fees and operational costs</li>
                </ol>
                <p>
                  The YILDXToken contract handles fee distribution to token holders. Individual holders can claim their accumulated fees through a dedicated function.
                </p>
              </Subsection>

              <Subsection id="gas-fund" title="Gas Fund">
                <p className="mb-4">
                  The gas fund serves as an operational treasury for the protocol. It covers:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Transaction gas costs for rebalancing</li>
                  <li>External contract interactions</li>
                  <li>Protocol maintenance and upgrades</li>
                </ul>
                <p>
                  The gas fund can only be used by governance decisions, ensuring proper oversight of operational expenses.
                </p>
              </Subsection>
            </Section>

            <Section id="governance-system" title="Governance System">
              <Subsection id="voting-power" title="Voting Power">
                <p className="mb-4">
                  Voting power is determined by the amount of YILDX tokens a holder controls. The system calculates:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>The total amount of voting power through token balances</li>
                  <li>The percentage of total governance power each holder has</li>
                </ul>
                <p className="mb-4">This system:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Aligns incentives with stake size</li>
                  <li>Creates a predictable governance distribution</li>
                  <li>Allows for delegation of voting power</li>
                </ul>
              </Subsection>

              <Subsection id="proposal-system" title="Proposal System">
                <p className="mb-4">
                  The governance contract implements a comprehensive proposal system. Creating a proposal requires:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>The proposer to have sufficient YILDX tokens</li>
                  <li>A proposal type specification</li>
                  <li>A description of the proposed change</li>
                  <li>The technical call data for implementation</li>
                </ul>
                <p className="mb-4">Proposals can include:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Fee adjustments</li>
                  <li>Token additions or removals</li>
                  <li>Allocation changes</li>
                  <li>Protocol upgrades</li>
                  <li>Emergency actions</li>
                </ul>
                <p>
                  Each proposal is voted on by token holders, with their voting power proportional to their token holdings.
                </p>
              </Subsection>

              <Subsection id="auto-approval-mechanism" title="Auto-Approval Mechanism">
                <p className="mb-4">
                  YildX implements an innovative auto-approval system that streamlines governance by:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>Calculating required voting thresholds based on proposal type</li>
                  <li>Checking if a strong majority (overwhelming support) exists</li>
                  <li>Automatically executing proposals when thresholds are met</li>
                  <li>Auto-rejecting proposals that mathematically cannot succeed</li>
                </ul>
                <p className="mb-4">This system provides several advantages:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Efficiency:</strong> Proposals can be approved and executed automatically when sufficient support is reached</li>
                  <li><strong>Fast-tracking:</strong> Critical proposals with overwhelming support can be executed immediately</li>
                  <li><strong>Auto-rejection:</strong> Proposals that mathematically cannot succeed are automatically rejected</li>
                  <li><strong>Type-specific thresholds:</strong> Different types of proposals can have different approval requirements</li>
                </ul>
              </Subsection>

              <Subsection id="delegation-system" title="Delegation System">
                <p className="mb-4">
                  YildX includes a comprehensive delegation system allowing token holders to:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>Delegate their voting power to trusted representatives</li>
                  <li>Create and update delegation profiles</li>
                  <li>Track voting history and approval rates</li>
                  <li>Choose delegates based on governance performance</li>
                </ul>
                <p className="mb-4">The delegation system enhances governance by:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Increasing participation from token holders who may not want to vote directly</li>
                  <li>Building reputation-based governance</li>
                  <li>Allowing for specialization among delegates</li>
                  <li>Creating more efficient governance processes</li>
                </ul>
              </Subsection>
            </Section>

            <Section id="security-considerations" title="Security Considerations">
              <Subsection id="smart-contract-security" title="Smart Contract Security">
                <p className="mb-4">The YildX contracts implement several security measures:</p>
                <ol className="list-decimal pl-6 space-y-2">
                  <li><strong>Inheritance from OpenZeppelin:</strong> Using battle-tested libraries for standard functions</li>
                  <li><strong>Access Control:</strong> Clear permission boundaries for different operations</li>
                  <li><strong>Input Validation:</strong> Thorough checks for parameters and state conditions</li>
                  <li><strong>External Call Protection:</strong> Proper handling of external contract interactions</li>
                </ol>
              </Subsection>

              <Subsection id="re-entrancy-protection" title="Re-entrancy Protection">
                <p>
                  All functions that handle ETH or token transfers use the ReentrancyGuard modifier, preventing re-entrancy attacks during the critical asset transfer operations.
                </p>
              </Subsection>

              <Subsection id="fee-safeguards" title="Fee Safeguards">
                <p className="mb-4">Fee parameters are bounded to prevent excessive fees:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Management fee cannot exceed 5%</li>
                  <li>Entry fee cannot exceed 1%</li>
                  <li>Exit fee cannot exceed 1%</li>
                </ul>
                <p>
                  These limits ensure that governance cannot set predatory fee levels.
                </p>
              </Subsection>
            </Section>

            <Section id="token-economics" title="Token Economics">
              <Subsection id="mcif-token-fund-token" title="MCIF Token (Fund Token)">
                <p className="mb-4">The MCIF token represents a share in the index fund:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Supply:</strong> Dynamic based on deposits and redemptions</li>
                  <li><strong>Value:</strong> Tracked to the underlying asset NAV</li>
                  <li><strong>Utility:</strong> Represents fund ownership, redeemable for ETH or assets</li>
                  <li><strong>Pricing:</strong> Determined by dividing the fund's total NAV by the token supply</li>
                </ul>
              </Subsection>

              <Subsection id="yildx-token-governance-token" title="YILDX Token (Governance Token)">
                <p className="mb-4">The YILDX token provides governance rights and fee income:</p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li><strong>Total Supply:</strong> 60,000,000 tokens</li>
                  <li><strong>Distribution:</strong>
                    <ul className="list-disc pl-6 mt-2">
                      <li>15,000,000 (25%) for initial sale</li>
                      <li>45,000,000 (75%) held in treasury for future governance initiatives</li>
                    </ul>
                  </li>
                  <li><strong>Fee Income:</strong> Holders receive 70% of all fees collected</li>
                  <li><strong>Voting Power:</strong> Proportional to token holdings</li>
                  <li><strong>Delegation:</strong> Ability to delegate voting power to other addresses</li>
                </ul>
              </Subsection>

              <Subsection id="initial-sale" title="YildX Token">
                <p className="mb-4">The initial token structure:</p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>Available only during active specific period</li>
                  <li>Limited to available tokens (15 million total)</li>
                  <li>Uses a tiered model with increasing tiers</li>
                  <li>Handles ETH transfers and token minting</li>
                  <li>Updates fee tracking for new token holders</li>
                </ul>
                <p className="mb-4">Key parameters:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Price:</strong> Tiered in ETH basis, increasing as more tokens supply reduces</li>
                  <li><strong>Minimum Minting:</strong> None</li>
                  <li><strong>Maximum Minting:</strong> Limited only by available tokens</li>
                  <li><strong>Funds Usage:</strong> Will be used for initial development and liquidity provision</li>
                </ul>
              </Subsection>
            </Section>

            <Section id="competitive-analysis" title="Competitive Analysis">
              <p className="mb-6">YildX introduces several innovations compared to existing DeFi index solutions:</p>
              
              <div className="overflow-x-auto mb-6">
                <table className="min-w-full bg-white border border-gray-200">
                  <thead>
                    <tr className="bg-indigo-50">
                      <th className="py-3 px-4 text-left font-semibold text-indigo-800 border-b">Feature</th>
                      <th className="py-3 px-4 text-left font-semibold text-indigo-800 border-b">YildX</th>
                      <th className="py-3 px-4 text-left font-semibold text-indigo-800 border-b">ETH2x</th>
                      <th className="py-3 px-4 text-left font-semibold text-indigo-800 border-b">hyETH</th>
                      <th className="py-3 px-4 text-left font-semibold text-indigo-800 border-b">BED Index</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="py-3 px-4 border-b">Asset Selection</td>
                      <td className="py-3 px-4 border-b">Community governance</td>
                      <td className="py-3 px-4 border-b">Centralized team</td>
                      <td className="py-3 px-4 border-b">Centralized team</td>
                      <td className="py-3 px-4 border-b">Centralized team</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 border-b">Fee Distribution</td>
                      <td className="py-3 px-4 border-b">70% to governance</td>
                      <td className="py-3 px-4 border-b">None</td>
                      <td className="py-3 px-4 border-b">None</td>
                      <td className="py-3 px-4 border-b">None</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 border-b">Governance Model</td>
                      <td className="py-3 px-4 border-b">Proportional voting</td>
                      <td className="py-3 px-4 border-b">None</td>
                      <td className="py-3 px-4 border-b">None</td>
                      <td className="py-3 px-4 border-b">None</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 border-b">Auto-Approval</td>
                      <td className="py-3 px-4 border-b">Yes</td>
                      <td className="py-3 px-4 border-b">None</td>
                      <td className="py-3 px-4 border-b">None</td>
                      <td className="py-3 px-4 border-b">None</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 border-b">Single-Asset Redemption</td>
                      <td className="py-3 px-4 border-b">Yes</td>
                      <td className="py-3 px-4 border-b">None</td>
                      <td className="py-3 px-4 border-b">None</td>
                      <td className="py-3 px-4 border-b">None</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 border-b">Fee Structure</td>
                      <td className="py-3 px-4 border-b">0.25% entry/exit + 2% annual</td>
                      <td className="py-3 px-4 border-b">3.65% annual</td>
                      <td className="py-3 px-4 border-b">20% Performance Fee</td>
                      <td className="py-3 px-4 border-b">0.25% annual</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <p className="mb-4">The YildX approach offers several advantages:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Aligned Incentives:</strong> Governance participants benefit directly from fund growth</li>
                <li><strong>Efficient Decision-Making:</strong> Majority Auto-approval speeds up governance</li>
                <li><strong>Fair Representation:</strong> Proportional voting balances power appropriately</li>
                <li><strong>Operational Sustainability:</strong> Gas fund ensures ongoing operation</li>
                <li><strong>Flexible Redemption:</strong> Users can choose how to exit their positions</li>
              </ul>
            </Section>

            <Section id="roadmap" title="Roadmap">
              <div className="space-y-8">
                <div className="bg-indigo-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-indigo-800 mb-4">Phase 1: Foundation (Completed)</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Smart contract development</li>
                    <li>MCIF index fund implementation</li>
                    <li>Governance infrastructure</li>
                    <li>Governance token</li>
                    <li>Decentralized governance system</li>
                  </ul>
                </div>
                
                <div className="bg-indigo-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-indigo-800 mb-4">Phase 2: Optimism Expansion (Current)</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Migration to Optimism Layer 2</li>
                    <li>Reduced gas fees and faster transactions</li>
                    <li>New perpetual swap income strategy</li>
                    <li>Enhanced governance tools</li>
                    <li>OP token integration</li>
                  </ul>
                </div>
                
                <div className="bg-indigo-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-indigo-800 mb-4">Phase 3: User-Owned Funds (Coming Soon)</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Create your own index fund with few clicks</li>
                    <li>Customizable allocation strategies</li>
                    <li>YILDX token-gated management features</li>
                    <li>Built on the existing YildX backbone</li>
                    <li>Custom fee structures</li>
                  </ul>
                </div>
                
                <div className="bg-indigo-50 rounded-lg p-6 mt-8">
                  <h3 className="text-xl font-semibold text-indigo-800 mb-4">Key Upcoming Features</h3>
                  <ul className="list-disc pl-6 space-y-4">
                    <li>
                      <strong>Perpetual Swap Interest Generation:</strong> Our upcoming funds will leverage perpetual swap protocols to generate sustainable interest income for fund participants.
                    </li>
                    <li>
                      <strong>Customizable Fund Templates:</strong> YILDX token holders will be able to choose from various fund templates or create their own with custom parameters, allocation strategies, and fee structures.
                    </li>
                  </ul>
                </div>
              </div>
            </Section>

            <Section id="conclusion" title="Conclusion">
              <p className="mb-4">
                YildX represents a significant advancement in decentralized asset management by combining the benefits of index fund investing with innovative governance and fee distribution mechanisms. Through the proportional voting model, auto-approval system, and aligned incentives, YildX creates a sustainable ecosystem where governance token holders are directly incentivized to support the fund's growth and success.
              </p>
              <p className="mb-4">
                By distributing 70% of all fees to governance token holders and using 30% to maintain the gas fund, YildX ensures both attractive passive income for YILDX token holders and operational sustainability for the protocol. This approach creates a virtuous cycle where fund growth benefits all participants and encourages long-term alignment with the protocol's success.
              </p>
              <p>
                YildX sets a new standard for decentralized fund management, offering a professional investment vehicle with true community governance.
              </p>
            </Section>

            <Section id="references" title="References">
              <ol className="list-decimal pl-6 space-y-2">
                <li>Ethereum ERC-20 Token Standard</li>
                <li>OpenZeppelin Smart Contract Libraries</li>
                <li>Uniswap V2/V3 Periphery Contracts</li>
                <li>Compound Governance Implementation</li>
                <li>Traditional Index Fund Methodologies</li>
              </ol>
            </Section>
          </div>
        </div>
      </div>
    </main>
  );
};

export default DocumentationPage;